import { useCallback, useEffect, useRef, useState } from "react";
import { App } from "../App";
import { ApplyShell } from "./ApplyShell";
import { DesktopRequired } from "./DesktopRequired";
import { FaqPage } from "./FaqPage";
import { Landing } from "./Landing";
import { LegalPage } from "./LegalPage";
import { OperatorShell } from "./OperatorShell";
import { Welcome } from "./Welcome";
import { acceptAgreement, getConfig, validateAccessCode } from "../lib/api";
import { resolveServer } from "../lib/server-config";
import { RESUME_DEAD_END_EVENT, loadActiveRunForCode } from "../lib/active-run";
import { parseDeepLink } from "../lib/deep-link";
import { normalizeAccessCode } from "../lib/access-code";

const ACCESS_CODE_STORAGE_KEY = "aurora.cloud.accessCode";
const WELCOMED_STORAGE_KEY = "aurora.cloud.welcomed";
const EMAIL_STORAGE_KEY = "aurora.cloud.email";

// Dev-only convenience: prefill the Landing code field so you don't
// retype a 16-char code every session. This is a PREFILL, not a bypass —
// Landing still shows and still validates the code against the backend,
// so you can edit it or enter a different one. Guarded by
// import.meta.env.DEV; production builds ignore it entirely.
const DEV_ACCESS_CODE: string = import.meta.env.DEV
  ? (import.meta.env.VITE_AURORA_CLOUD_DEV_ACCESS_CODE ?? "").trim()
  : "";

function readStoredCode(): string | null {
  // localStorage first, then sessionStorage. The fallback is for private
  // browsing: Safari evicts backgrounded private tabs and reloads them
  // with localStorage gone, which used to bounce a mid-wizard user all
  // the way back to access-code entry (live, 2026-06-11). sessionStorage
  // usually survives a same-tab reload even in private mode, so the
  // eviction lands on "Drop your messages" with the code intact instead.
  for (const store of [
    () => localStorage.getItem(ACCESS_CODE_STORAGE_KEY),
    () => sessionStorage.getItem(ACCESS_CODE_STORAGE_KEY),
  ]) {
    try {
      const raw = store();
      if (raw && typeof raw === "string" && raw.length > 0) return raw;
    } catch {
      // storage unavailable — try the next one / fall through to landing
    }
  }
  return null;
}

function readWelcomed(): boolean {
  try {
    return localStorage.getItem(WELCOMED_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

// A code can arrive pre-filled from the marketing site's "Enter your code"
// card (plaintosee.cc → pilot.plaintosee.cc/#code=…). It rides the URL HASH,
// not the query string, so the credential is never sent to the server or
// captured in access logs / the Referer header — the same channel, and same
// reason, as the ready-email deep-link token (see deep-link.ts). Like
// DEV_ACCESS_CODE this is a PREFILL, not a bypass — Landing still validates
// the code against the backend — and the effect below strips it from the URL.
function readUrlCode(): string {
  if (typeof window === "undefined") return "";
  try {
    const hash = window.location.hash.replace(/^#/, "");
    const raw = new URLSearchParams(hash).get("code");
    return raw ? raw.trim() : "";
  } catch {
    return "";
  }
}


// Match `prefix` and `prefix/*` exactly, NOT `prefix-help` / `prefixial`.
// A naive `startsWith` would be a credential-phishing escape hatch on any
// future route that happens to share the literal prefix. Use this helper
// for every path discriminator so the next person adding one can't
// reach for `startsWith` and silently regress the contract.
function matchPathSegment(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(prefix + "/");
}

function isOperatorPath(): boolean {
  if (typeof window === "undefined") return false;
  return matchPathSegment(window.location.pathname, "/operator");
}

function isApplyPath(): boolean {
  if (typeof window === "undefined") return false;
  return matchPathSegment(window.location.pathname, "/apply");
}

function isTermsPath(): boolean {
  if (typeof window === "undefined") return false;
  return matchPathSegment(window.location.pathname, "/terms");
}

function isPrivacyPath(): boolean {
  if (typeof window === "undefined") return false;
  return matchPathSegment(window.location.pathname, "/privacy");
}

function isFaqPath(): boolean {
  if (typeof window === "undefined") return false;
  return matchPathSegment(window.location.pathname, "/faq");
}

// Below this width the wizard's multi-column layout no longer fits, so a
// narrow desktop window is blocked alongside actual mobile devices.
const MOBILE_MAX_WIDTH = 900;

// The wizard needs a desktop/laptop — the user uploads a PDF and the
// deliverable is a file they save + re-open offline, both poor-to-broken on
// phones/tablets. Block on either signal: a mobile/tablet OS (catches big
// tablets a viewport check would miss), OR a window narrower than the wizard
// layout needs (catches narrow desktop windows). The public routes above the
// gate (/apply, /terms, /privacy) stay readable regardless.
function shouldBlockWizard(): boolean {
  if (isMobileDevice()) return true;
  if (typeof window !== "undefined" && window.innerWidth < MOBILE_MAX_WIDTH) {
    return true;
  }
  return false;
}

// Catch phones + tablets, including the iPadOS case where Safari reports a
// desktop "Macintosh" UA (real Macs have no touch screen).
function isMobileDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  const uaData = (navigator as Navigator & {
    userAgentData?: { mobile?: boolean };
  }).userAgentData;
  if (uaData && uaData.mobile === true) return true;
  const ua = navigator.userAgent || "";
  if (
    /Android|iPhone|iPod|iPad|Mobile|Silk|Kindle|BlackBerry|Opera Mini|IEMobile|Tablet|PlayBook/i.test(
      ua,
    )
  ) {
    return true;
  }
  // iPadOS 13+ masquerades as desktop Safari ("Macintosh"); a touch screen
  // distinguishes it from a real Mac.
  if (
    /Macintosh/.test(ua) &&
    typeof navigator.maxTouchPoints === "number" &&
    navigator.maxTouchPoints > 1
  ) {
    return true;
  }
  return false;
}

export function Shell() {
  // Code handed off from the marketing site (#code=…), NORMALIZED the same way
  // Landing normalizes before it stores — so the equality check below treats a
  // correct code that differs only in case/spacing as a match (not a different
  // code). Read once so a later URL change can't re-seed it; the effect below
  // strips it from the URL.
  const [urlCode] = useState<string>(() => normalizeAccessCode(readUrlCode()));
  // A ready-email deep link (#run=…&token=…) is its own capability — the
  // run-token is the auth — so it bypasses the Landing/Welcome gate and App's
  // DeepLinkLanding takes over. Without this, the link could NEVER work for a
  // completed run: the access code is consumed at run start, so the mount
  // revalidation below would clear it and strand the user on Landing (their
  // used code no longer validates). Found in prod 2026-06-09. Read once via
  // the initializer so later hash changes can't flip the entry decision.
  const [deepLinked] = useState<boolean>(
    () =>
      typeof window !== "undefined" &&
      parseDeepLink(window.location.hash) !== null,
  );
  // Stored session, read once and shared by both initializers below.
  const [storedCode] = useState(readStoredCode);
  // An explicit #code= that DIFFERS from a stored session is an intent to enter
  // as THAT code — route it through Landing for validation instead of silently
  // resuming the stored session. (A returning user with a completed run was
  // dropped straight into that run after pasting a wrong/incomplete code —
  // prod, 2026-06-18.) A matching code preserves the resume path (incl. the
  // used-code-with-active-run case the mount revalidation protects); and a
  // deep-link entry never overrides — its run-token, not the code, is the auth.
  const overrideStored =
    !deepLinked && urlCode !== "" && urlCode !== storedCode;
  const [accessCode, setAccessCode] = useState<string | null>(() =>
    overrideStored ? null : storedCode,
  );
  const [welcomed, setWelcomed] = useState<boolean>(() =>
    overrideStored ? false : readWelcomed(),
  );
  const [wizardBlocked, setWizardBlocked] = useState<boolean>(shouldBlockWizard);
  // Door state (self_serve_open), fetched exactly once per page load by
  // Shell itself — regardless of entry path. This USED to be Landing's job
  // (Landing fetched /config and relayed the answer up via a callback), but
  // an entry path that skips Landing entirely — a returning visitor
  // restored straight into Welcome, code already stored — never gave
  // Landing the chance to fetch, so this stayed at its default (false) and
  // Welcome kept showing the "Early access" pill even once the door was
  // open (live on staging, 2026-07-04). Shell owning the fetch means every
  // entry path gets the same answer. Defaults false (early-access posture)
  // until the fetch resolves — same graceful-fallback default the old
  // Landing-owned fetch used.
  const [selfServeOpen, setSelfServeOpen] = useState(false);
  // True once the user has explicitly backed out of the Welcome/gate (mast-
  // logo click or browser Back) rather than arriving at Landing fresh.
  // Lossless: accessCode/welcomed below are NEVER touched by this — it only
  // changes which screen Shell renders. Cleared by handleAccept so a code
  // accepted from the escaped-to-Landing view advances normally instead of
  // getting stuck back on Landing (2026-07-04 escape-hatch fix).
  const [gateEscaped, setGateEscaped] = useState(false);

  // Re-evaluate the desktop gate on resize so a desktop user who shrinks or
  // grows their window crosses the threshold live (the OS signal can't
  // change mid-session, but the viewport one can).
  useEffect(() => {
    function onResize() {
      setWizardBlocked(shouldBlockWizard());
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Strip #code= from the URL hash after it's captured — the access code is a
  // credential and shouldn't linger in the address bar, history, or a copied
  // link. Only the `code` key is removed; any other hash content is preserved
  // (a deep-link #run=…&token=… never carries a code, so it's untouched).
  useEffect(() => {
    if (!urlCode || typeof window === "undefined") return;
    try {
      const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
      params.delete("code");
      const rest = params.toString();
      const cleaned =
        window.location.pathname + window.location.search + (rest ? "#" + rest : "");
      window.history.replaceState(null, "", cleaned);
    } catch {
      // history API unavailable — harmless; the param just stays in the URL
    }
  }, [urlCode]);

  // Re-validate a cached code against the server, applying the same
  // "eject on revoked/expired/unknown, tolerate used/no_fresh_starts"
  // handling everywhere this is called from: mount, tab-visibility
  // recovery, AND resume-from-escape (2026-07-04, Finding 4 — see the
  // onResume call site below). Kept as a stable useCallback (empty deps;
  // it closes over nothing but setters, which React guarantees are
  // stable) so effects that depend on it don't re-run just because the
  // component re-rendered. `isCancelled` lets a caller that can go away
  // mid-flight (the mount effect, across unmount) suppress a late result;
  // callers that don't need that (the resume handler) just accept the
  // default no-op.
  const revalidateAccessCode = useCallback(
    (code: string, isCancelled: () => boolean = () => false) => {
      // Snapshot the run pointer NOW: App's resume probe runs concurrently
      // and clears the pointer on transient failures, so reading it only at
      // validate-resolve time would lose the race and eject anyway.
      const hadRunAtCheck = loadActiveRunForCode(code) !== null;
      validateAccessCode(resolveServer(), code)
        .then((res) => {
          if (isCancelled()) return;
          if (!res.valid) {
            // "used" is the NORMAL state of this browser's code once its
            // run has launched (codes are consumed at run start). If this
            // browser holds the run pointer for that code, the user's
            // place IS the run — ejecting to Landing here ("Code already
            // used") destroyed the most-encouraged action in the flow:
            // close the tab during a batched run and come back (audit B4).
            // Eject only on revoked/expired/unknown, or on a used code
            // with nothing to resume (someone else's code, typed fresh).
            if (
              res.reason === "used" &&
              (hadRunAtCheck || loadActiveRunForCode(code) !== null)
            ) {
              return;
            }
            // "no_fresh_starts" is a FRONT-DOOR verdict, not a session
            // revocation: Landing refuses fresh entry of a spent code, and
            // /runs/preview 429s a third start (CapBlockedPanel takes over
            // in-wizard). Ejecting here turned every app switch into a
            // kick-to-Landing once a code spent its starts — and the
            // counter hits the cap the moment the ALLOWED second start
            // launches, so it could yank a user out of a legitimately
            // running preview (live, 2026-06-11). In-session: never eject.
            if (res.reason === "no_fresh_starts") {
              return;
            }
            try {
              localStorage.removeItem(ACCESS_CODE_STORAGE_KEY);
              sessionStorage.removeItem(ACCESS_CODE_STORAGE_KEY);
              // A revoked-then-re-issued cycle must redo the Welcome
              // handshake (new email, new caveats ack) — clear both.
              localStorage.removeItem(WELCOMED_STORAGE_KEY);
              localStorage.removeItem(EMAIL_STORAGE_KEY);
            } catch {
              // ignore
            }
            setAccessCode(null);
            setWelcomed(false);
          }
        })
        .catch(() => {
          // Network/CORS failure: don't lock the user out, they may be
          // offline. The server-side check on actual run will catch a
          // revoked code at LLM-spend time.
        });
    },
    [],
  );

  // Re-validate the cached code against the server on mount AND whenever
  // the tab becomes visible again. Otherwise revocation only blocks
  // fresh Landing entries — a user whose code was revoked while their
  // tab was backgrounded would walk straight into the wizard on return.
  // The redemption-on-run check in a later branch will close the LLM-
  // spend hole; this closes the UX hole.
  useEffect(() => {
    if (
      !accessCode ||
      deepLinked || // deep-link entry: the (used) code must not be cleared under it
      isOperatorPath() ||
      isApplyPath() ||
      isTermsPath() ||
      isPrivacyPath() ||
      isFaqPath()
    )
      return;
    let cancelled = false;
    function runCheck() {
      revalidateAccessCode(accessCode!, () => cancelled);
    }

    runCheck();

    function handleVisibility() {
      if (document.visibilityState === "visible") {
        runCheck();
      }
    }
    document.addEventListener("visibilitychange", handleVisibility);
    // App's resume probe found nothing to restore (consumed/unknown state,
    // or a 404/410 purge). The mount check tolerated "used" BECAUSE the run
    // pointer promised a resume; that promise just broke, so re-run the
    // verdict — a used code with nothing left to resume now ejects to
    // Landing instead of stranding the user on an empty wizard with no
    // code entry (live on staging, 2026-08-06).
    window.addEventListener(RESUME_DEAD_END_EVENT, runCheck);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener(RESUME_DEAD_END_EVENT, runCheck);
    };
    // accessCode IS a dependency (review fix, 2026-08-06): these listeners
    // close over it, and a session can switch codes without a page load
    // (eject → Landing → new code). A stale closure here judged the NEW
    // session by the OLD code's "used" verdict on the next refocus or
    // dead-end event and ejected a valid session, wiping the fresh code
    // from storage. Re-registering per code keeps the verdict honest.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revalidateAccessCode, accessCode]);

  // Fetch the door state (self_serve_open) exactly once per page load,
  // regardless of entry path — see the selfServeOpen state comment above
  // for why this moved here from Landing. Skipped on the same public/
  // legal/operator/deep-link routes the access-code revalidation effect
  // above skips: those never render Landing or Welcome, so the answer
  // would go unused.
  useEffect(() => {
    if (
      deepLinked ||
      isOperatorPath() ||
      isApplyPath() ||
      isTermsPath() ||
      isPrivacyPath() ||
      isFaqPath()
    )
      return;
    let cancelled = false;
    getConfig(resolveServer())
      .then((cfg) => {
        if (cancelled) return;
        setSelfServeOpen(cfg.self_serve_open === true);
      })
      .catch(() => {
        // Graceful fallback — stay in the CLOSED (early-access) posture,
        // the same default the old Landing-owned fetch used on failure.
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Escape-hatch history model (2026-07-04) --------------------------
  //
  // "atGate" mirrors exactly the condition that renders Welcome below —
  // kept as its own const so the history-push effect and the popstate
  // handler agree with the render branch on what "sitting at the gate"
  // means, without duplicating slightly-different logic in three places.
  const onEscapeExemptRoute =
    isOperatorPath() ||
    isApplyPath() ||
    isTermsPath() ||
    isPrivacyPath() ||
    isFaqPath() ||
    deepLinked;
  const atGate =
    !onEscapeExemptRoute &&
    !wizardBlocked &&
    accessCode !== null &&
    !welcomed &&
    !gateEscaped;

  // The gate is the ONLY screen that ever gets a history entry pushed for
  // it — push once per PAGE LOAD, not once per arrival. gatePushedRef only
  // flips back to false when that entry is actually CONSUMED by a real
  // history pop (see the popstate handler below, the only other place that
  // touches it) — never merely because atGate went false. A JS-driven
  // departure from the gate (mast-logo escape, a resize crossing the
  // wizard-blocked threshold, even a later fresh arrival via a brand-new
  // code) leaves the previously-pushed entry sitting untouched on the
  // stack, so treating "atGate went false" as "the entry is gone" and
  // re-pushing on the next arrival stacked one extra history entry per
  // escape/resume cycle — each stacked entry meant one more Back press
  // needed to actually leave the app (2026-07-04, Finding 3). Since every
  // push here writes the SAME url (window.location.href, unchanged), the
  // browser's position never actually needs to move for any of this to
  // work — there's nothing to "re-arm" until a real pop consumes the one
  // entry that's there.
  const gatePushedRef = useRef(false);
  useEffect(() => {
    if (atGate && !gatePushedRef.current) {
      window.history.pushState({ auroraGate: true }, "", window.location.href);
      gatePushedRef.current = true;
    }
  }, [atGate]);

  // Browser Back from the gate must do the SAME lossless escape the mast-
  // logo click performs ("people always click back" — Rich, 2026-07-04).
  // Wizard/run screens deliberately never push a history entry of their
  // own (a live analysis must never be torn down by an in-app Back), so a
  // pop that arrives while welcomed is true is popping the gate's entry (or
  // whatever preceded it) out from under an unrelated screen — that's
  // trapped by re-pushing immediately, the standard SPA back-trap. This can
  // take more than one Back press to actually leave the app from mid-
  // wizard; that's deliberate, and separate from the tab-close guard
  // (lib/unload-guard.ts), which covers full-page unload rather than
  // in-app history pops.
  //
  // Forward from an escaped Landing back onto the (still-present) gate
  // entry deliberately does NOT auto-restore Welcome — the explicit
  // "continue where you left off" affordance (Landing's resumeAvailable)
  // is the one documented way back in, rather than also reasoning about
  // pop direction here.
  useEffect(() => {
    function onPopState() {
      if (onEscapeExemptRoute) return;
      if (welcomed) {
        window.history.pushState({ auroraGate: true }, "", window.location.href);
        return;
      }
      if (accessCode !== null && !gateEscaped) {
        // This pop just consumed the gate's pushed history entry — re-arm
        // so a LATER genuine arrival at the gate (not a same-cycle resume)
        // pushes its own fresh entry rather than assuming one is already
        // sitting on the stack (2026-07-04, Finding 3).
        gatePushedRef.current = false;
        setGateEscaped(true);
      }
    }
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [welcomed, accessCode, gateEscaped, onEscapeExemptRoute]);

  // The operator surface runs at /operator/* and is a separate flow
  // from the wizard. Server-side gate is the Phase 5 hybrid: CF Access
  // OTP fronts the URL, and the server accepts either a verified CF
  // Access JWT (cookie) OR a matching X-Operator-Token. OperatorShell
  // probes for the JWT path on mount and only shows the token-entry
  // form if that probe fails (localhost dev, non-CF callers).
  if (isOperatorPath()) {
    return <OperatorShell />;
  }

  // /apply is the public application form — outside the access-code
  // gate by design (an applicant doesn't have a code yet).
  if (isApplyPath()) {
    return <ApplyShell />;
  }

  // /terms and /privacy are the public legal pages — readable without an
  // invite, and the target of the agreement screen's "I agree" links.
  if (isTermsPath()) {
    return <LegalPage kind="terms" />;
  }
  if (isPrivacyPath()) {
    return <LegalPage kind="privacy" />;
  }

  // /faq is the public help page — readable without an invite, same posture
  // as /terms and /privacy.
  if (isFaqPath()) {
    return <FaqPage />;
  }

  function handleAccept(code: string) {
    // A DIFFERENT code than the one we welcomed under: the clickwrap acceptance
    // was recorded for the OLD code, so force Welcome again for the new one. A
    // returning browser carries `welcomed==="1"` from a prior code; without this
    // it skips the clickwrap and can reach a preview (which ships the corpus to
    // Anthropic) with no recorded consent for the new code — the server 403s at
    // /runs/preview + /runs/start, but re-showing the clickwrap is the graceful
    // path, not a 403 (2026-07-07 review, ③). Same code re-entered: leave
    // `welcomed` alone (resume shouldn't re-prompt).
    if (code !== accessCode) {
      setWelcomed(false);
      try {
        localStorage.removeItem(WELCOMED_STORAGE_KEY);
      } catch {
        // ignore — welcomed also lives in React state above
      }
    }
    // Both stores: sessionStorage is the private-browsing eviction net
    // (see readStoredCode).
    try {
      localStorage.setItem(ACCESS_CODE_STORAGE_KEY, code);
    } catch {
      // ignore — code may still land in sessionStorage below
    }
    try {
      sessionStorage.setItem(ACCESS_CODE_STORAGE_KEY, code);
    } catch {
      // ignore — code will live in-memory for this session
    }
    setAccessCode(code);
    // A code accepted from the escaped-to-Landing view (or a fresh entry —
    // this is a no-op there, already false) should advance normally rather
    // than getting stuck back on Landing because gateEscaped is still true.
    setGateEscaped(false);
  }

  async function handleWelcomeStart() {
    if (accessCode) {
      // Record the clickwrap before the wizard opens. Throw on failure so
      // Welcome surfaces the inline retry and does NOT advance. Use
      // resolveServer() — the SAME backend the runs go to — not DEFAULT_SERVER:
      // recording acceptance on one server while /runs/{preview,start} hit
      // another (a SERVER_STORAGE_KEY override, or localhost dev) lands the
      // acceptance in the wrong DB and the gate re-shows forever
      // (2026-07-07 review, ⑧).
      await acceptAgreement(resolveServer(), accessCode);
    }
    try {
      localStorage.setItem(WELCOMED_STORAGE_KEY, "1");
    } catch {
      // localStorage blocked — stash in memory for this session
    }
    setWelcomed(true);
  }

  // "Continue where you left off" — resumes purely via local state
  // (gateEscaped flips back to false), so without a fresh check here a
  // code revoked while the user sat on the escaped Landing view would
  // silently wave them back into Welcome (2026-07-04, Finding 4). Fires
  // the SAME revalidation the mount/visibility paths use; on an invalid
  // verdict it clears accessCode/welcomed exactly as they do, which lands
  // the render back on plain Landing (no resume affordance, since that
  // requires accessCode !== null) rather than Welcome.
  function handleResume() {
    setGateEscaped(false);
    if (accessCode) revalidateAccessCode(accessCode);
  }

  // Deep-link entry skips Landing/Welcome entirely — App sees the fragment
  // and renders DeepLinkLanding (the gate-free doorway to a finished result).
  // MUST beat the desktop gate below: the ready email is opened on phones
  // more than anywhere, and DeepLinkLanding has its own mobile handling
  // (come-back-on-a-desktop hint; the link keeps working for the full
  // window). DesktopRequired took over instead and its copy/email-myself
  // affordances carry the BARE pilot URL — the fragment (run + token) was
  // stripped, stranding the user at the gate on their computer with a
  // used code (live, 2026-06-11).
  if (deepLinked) {
    return <App />;
  }

  // Gate the wizard (Landing → Agreement → run) on mobile or a too-narrow
  // window. The public routes above (/apply, /terms, /privacy) stay readable
  // — only the upload/run/download flow requires a computer. The invite code
  // isn't consumed until run-launch, so a blocked user keeps their code and
  // resumes on a desktop.
  if (wizardBlocked) {
    return <DesktopRequired />;
  }

  // gateEscaped forces Landing even with a stored accessCode present — the
  // explicit "back to Landing" escape from Welcome (mast-logo click or
  // browser Back). resumeAvailable/onResume surface the "continue where
  // you left off" affordance so the saved session isn't a re-type away.
  if (!accessCode || gateEscaped) {
    return (
      <Landing
        onAccept={handleAccept}
        initialCode={urlCode || DEV_ACCESS_CODE}
        selfServeOpen={selfServeOpen}
        resumeAvailable={gateEscaped && accessCode !== null}
        onResume={handleResume}
      />
    );
  }
  if (!welcomed) {
    return (
      <Welcome
        onStart={handleWelcomeStart}
        selfServeOpen={selfServeOpen}
        onGoHome={() => setGateEscaped(true)}
      />
    );
  }
  return <App />;
}
